import { i18n } from "../boot/i18n";
import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import Email from "./Email";
import { deepFreeze, timestampToDate } from "../util/misc";
import { isArray, isString } from "lodash";

export const FEEDBACK_FIELDS = {
  // Feedback info
  contactID: "",
  company: "",
  answers: null,
  sent: null,
  sentTo: null,
  responded: null,
};

export const FEEDBACK_QUESTIONS = [
  {
    key: "overallSatisfaction",
    type: "radio",
  },
  {
    key: "serviceSatisfaction",
    type: "checkbox",
    hasOther: true,
  },
  {
    key: "serviceDissatisfaction",
    type: "checkbox",
    hasOther: true,
  },
  {
    key: "requests",
    type: "textarea",
  },
  {
    key: "greatestWeakness",
    type: "textarea",
  },
  { key: "restaurant", type: "text" },
];

// Default answers
let answers = {};
FEEDBACK_QUESTIONS.forEach((q, i) => {
  if (q.type === "checkbox") {
    answers[i] = [];
  } else {
    answers[i] = "";
  }
  if (q.hasOther) {
    answers[i + "-other"] = "";
  }
});
FEEDBACK_FIELDS.answers = answers;
deepFreeze(FEEDBACK_FIELDS);
deepFreeze(FEEDBACK_QUESTIONS);

export default class Feedback extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, FEEDBACK_FIELDS);

    // Convert to Date
    data.sent = timestampToDate(data.sent);
    data.responded = timestampToDate(data.responded);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      view: Feedback.canBeViewedBy,
      create: Feedback.canBeCreatedBy,
      edit: Feedback.canBeEditedBy,
      delete: Feedback.canBeDeletedBy,
    });
  }

  // Permissions

  static canBeViewedBy(user) {
    return user.isAdmin;
  }

  static canBeCreatedBy(user) {
    return user.isAdmin;
  }

  static canBeEditedBy(user) {
    return user.isAdmin;
  }

  static canBeDeletedBy(user) {
    return user.isAdmin;
  }

  // Database

  get path() {
    return Feedback.getPath(this.id);
  }

  static getPath(id) {
    let path = `feedback`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(feedback) {
    return feedback.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Feedback(snapshot.id, data);
  }

  // Editing

  get percentComplete() {
    let complete = 0;
    FEEDBACK_QUESTIONS.forEach((q, i) => {
      let answer = this.data.answers[i];
      if (q.type === "checkbox") {
        complete +=
          answer.length > 0 ||
          (q.hasOther &&
            this.data.answers[i + "-other"] !== null &&
            this.data.answers[i + "-other"].trim().length > 0);
      } else {
        complete +=
          answer !== null && (!isString(answer) || answer.trim().length > 0);
      }
    });
    return complete / FEEDBACK_QUESTIONS.length;
  }

  get isValid() {
    return this.percentComplete === 1;
  }

  // Formatting

  get sentTo() {
    return this.data.sentTo ? new Email(this.data.sentTo) : null;
  }

  get contact() {
    return this.data.contactID
      ? store.state.main.contacts[this.data.contactID] || null
      : null;
  }

  get questions() {
    return FEEDBACK_QUESTIONS.map((q) => {
      const output = {
        // Pass a shallow copy, never this.data itself: vue-i18n runs with
        // escapeParameterHtml, and its escapeParams() mutates the named-params
        // object in place (named[key] = escapeHtml(named[key])). this.data is
        // deep-frozen in the store, so that write throws ("Cannot assign to
        // read only property 'contactID'"); on the live customer-form buffer
        // it would silently double-escape the values on every render.
        ...q,
        text: i18n.global.t(`feedback.customer.${q.key}.question`, {
          ...this.data,
        }),
      };
      if (q.type === "radio" || q.type === "checkbox") {
        // vue-i18n 9: `tm()` returns the raw message tree at a key path.
        // The `i18n.messages[i18n.locale]` access pattern was vue-i18n 8.
        output.options = i18n.global.tm(`feedback.customer.${q.key}.options`);
      }
      return output;
    });
  }

  get answers() {
    return FEEDBACK_QUESTIONS.map((q, i) => {
      let text, other;
      let answer = this.data.answers[i];
      if (!answer) {
        text = "";
      } else {
        if (!isArray(answer)) {
          answer = [answer];
        }

        // Map values to labels
        if (q.type === "radio" || q.type === "checkbox") {
          const options = i18n.global.tm(`feedback.customer.${q.key}.options`);
          answer = answer.map(
            (value) => options.find((o) => o.value === value).label
          );
        }

        // Append 'other' values
        if (q.hasOther) {
          other = this.data.answers[`${i}-other`];
          if (other) {
            answer.push(other);
          }
        }

        // Convert to string
        text = answer.join("\n");
      }

      return {
        question: {
          ...q,
          // Shallow copy, not this.data — see the questions getter above.
          text: i18n.global.t(`feedback.customer.${q.key}.question`, {
            ...this.data,
          }),
        },
        text,
      };
    });
  }
}
